import { Button } from '@evaneos/b2b-ui/legacy';
import cx from 'classnames';
import { addDays, isAfter } from 'date-fns';
import React, { forwardRef, SyntheticEvent, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { DraggableAttributes } from '@dnd-kit/core/dist/hooks/useDraggable';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

import { Replay } from '../../../design-system/Icons/Replay';
import { Loader } from '../../../design-system/Loader';
import { RepeaterBadge } from '../../../design-system/RepeaterBadge/RepeaterBadge';
import { ABMaturityLatencyExperimentChip } from '../../../shared/components/ExperimentChip';
import { MessageWithNotification } from '../../../design-system/SVG/MessageWithNotification';
import { SALES_PROCESS } from '../../../domain';
import { NEW_REQUEST_STEP, DISCOVERY_STEP } from '../../../domain/step';
import { Avatar } from '../../../resources/agent/components/Avatar';
import { NormalizedAgents } from '../../../resources/agent/types';
import { hasUnreadMessage } from '../../../resources/dossier/operations';
import {
    DossierId,
    DossierOverview as DossierOverviewType,
} from '../../../resources/dossier/types';
import { AllTranslationKeys } from '../../../shared/intl/types';
import { getToday } from '../../../shared/utilities/date';
import { HeatChip } from '../../../shared/components/DossierQualification/HeatChip';
import { TagsTooltipChip } from '../../../shared/components/DossierQualification/TagsTooltipChip';
import {
    ChangeStepMutationVariables,
    useChangeStepStatus,
} from '../../../resources/dossier/mutations';
import { getFeatureFlag } from '../../../shared/feature-flags/getFeatureFlag';
import s from '../Process.css';

import { Project } from './Project';

export type Props = {
    id: DossierId;
    dossier: DossierOverviewType;
    agents: NormalizedAgents | undefined;
    displayAvatar: boolean;
    isDragging: boolean;
    isLatestUpdated: boolean;
    onRetry?: (params: ChangeStepMutationVariables) => void;
    setLatestUpdated?: (dossierId: DossierId) => void;
    hasMultipleMarkets?: boolean;
    isDragOverlay?: boolean;
    attributes?: DraggableAttributes;
    listeners?: SyntheticListenerMap | undefined;
};

const DossierOverviewBase = forwardRef<HTMLDivElement, Props>(
    (
        {
            agents,
            displayAvatar,
            hasMultipleMarkets,
            isDragging,
            isLatestUpdated,
            dossier,
            onRetry,
            setLatestUpdated,
            attributes,
            listeners,
            id,
            isDragOverlay,
        },
        drag,
    ) => {
        const [isTooltipOpen, setTooltipOpen] = useState(false);
        const isAbMaturityLatencyExperimentEnabled = getFeatureFlag('abMaturityLatencyExperiment');

        const { isPending, isError, stepInError } = useChangeStepStatus(id);

        const handleMouseEnter = () => !isDragging && setTooltipOpen(true);
        const handleMouseLeave = () => setTooltipOpen(false);

        const handleRetry = (e: SyntheticEvent<HTMLButtonElement>): void => {
            e.preventDefault();
            if (isError && stepInError) {
                onRetry?.({
                    dossierId: id,
                    nextStep: stepInError,
                });
                setLatestUpdated?.(id);
            }
        };

        const {
            project,
            task,
            step,
            process,
            agentId,
            rating,
            isDuplicate,
            budget,
            requesterIsRepeater,
            heatLevel,
            tags,
        } = dossier;
        const currentDate = getToday();
        const isDueDateLate: boolean = isAfter(currentDate, addDays(task.dueDate, 1));
        const isArchived = Boolean(dossier.archiveDate);
        const isDisplayingTask = !(isArchived || task.definition === 'trip_has_ended.trip_ended');

        return (
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                id={id}
                key={id}
                ref={drag}
                style={{
                    opacity: isDragging && !isDragOverlay ? 0.3 : 1,
                    marginBottom: '4px',
                }}
                {...attributes}
                {...listeners}
            >
                <Link
                    data-testid={`dossier-overview-id-${id}`}
                    to={{
                        pathname: `/dossier/${id}/discussion`,
                    }}
                >
                    <div
                        className={cx(s.dossierContainer, {
                            [s.latestUpdated]: isLatestUpdated,
                            [s.cardDuplicate]: isDuplicate,
                        })}
                    >
                        {isPending && (
                            <div className={s.overlay}>
                                <Loader />
                            </div>
                        )}
                        {isError && (
                            <div className={s.overlay}>
                                <Button
                                    priority="secondary"
                                    onClick={handleRetry}
                                    className={s.retryButton}
                                    data-testid="retry-button"
                                >
                                    <Replay color="var(--ink)" />
                                    <FormattedMessage id={`global.retry`} />
                                </Button>
                            </div>
                        )}
                        {isDisplayingTask && (
                            <div className={s.taskContainer}>
                                {process === SALES_PROCESS && (
                                    <>
                                        <HeatChip heatLevel={heatLevel} isSelected />
                                        <TagsTooltipChip
                                            tags={tags}
                                            isTooltipOpen={isTooltipOpen}
                                        />
                                    </>
                                )}
                                {isAbMaturityLatencyExperimentEnabled &&
                                project.maturityExperiments?.isLatencyTest &&
                                (step === NEW_REQUEST_STEP || step === DISCOVERY_STEP) ? (
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <ABMaturityLatencyExperimentChip size="small" />
                                    </div>
                                ) : null}
                                <div className={s.shrinkable}>
                                    <p className={cx('badge', s.task, s.taskDefinition)}>
                                        <span>
                                            <FormattedMessage
                                                id={
                                                    `tasks.${task.definition}.name` as AllTranslationKeys
                                                }
                                            />
                                        </span>
                                    </p>
                                </div>

                                <div className={s.rightTopCorner}>
                                    {requesterIsRepeater && <RepeaterBadge />}
                                    <div
                                        className={cx(s.task, {
                                            [s.lateTask]: isDueDateLate,
                                            [s.todayTask]: !isDueDateLate,
                                        })}
                                        data-testid={`dossier-overview-id-${id}-due-date-late`}
                                    >
                                        <p className={cx('badge', s.taskDueDate)}>
                                            <FormattedDate
                                                value={task.dueDate}
                                                day="2-digit"
                                                month="short"
                                            />
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <Project
                            hasMultipleMarkets={hasMultipleMarkets}
                            displayRating={false}
                            rating={rating}
                            adjustedBudget={budget}
                            isDuplicate={isDuplicate}
                            requesterIsRepeater={requesterIsRepeater}
                            {...project}
                        />
                        <div className={s.rightBottomCorner}>
                            {displayAvatar && agents && (
                                <Avatar
                                    name={agents[agentId].name}
                                    photoUrl={agents[agentId].photoUrl}
                                />
                            )}
                            {hasUnreadMessage(dossier) && (
                                <MessageWithNotification data-testid="dossier-overview-unread-message" />
                            )}
                        </div>
                    </div>
                </Link>
            </div>
        );
    },
);

export const DossierOverview = React.memo(DossierOverviewBase, (prevProps, nextProps) => {
    const {
        displayAvatar,
        isDragging,
        isLatestUpdated,
        agents,
        dossier: {
            task: { definition, dueDate },
        },
    } = prevProps;
    return (
        definition === nextProps.dossier.task.definition &&
        dueDate === nextProps.dossier.task.dueDate &&
        isDragging === nextProps.isDragging &&
        isLatestUpdated === nextProps.isLatestUpdated &&
        agents === nextProps.agents &&
        displayAvatar === nextProps.displayAvatar
    );
});
